/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  IconButton,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-hot-toast';
import RecordPayment from './RecordPayment';
import InvoiceDetail from './InvoiceDetail';
import {
  DatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useFacility from '../../hooks/useFacility';

// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const AccountsReceivable = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [facilities, setFacilities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  useEffect(() => {
    fetchFacilities();
    fetchAllInvoices();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      toast.error('Failed to fetch facilities');
    }
  };

  const fetchAllInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/invoices');
      // Flatten invoices from all projects
      const allInvoices = [];
      response.data.forEach(invoiceDoc => {
        if (invoiceDoc.invoices && invoiceDoc.invoices.length > 0) {
          invoiceDoc.invoices.forEach(invoice => {
            allInvoices.push({
              ...invoice.toObject ? invoice.toObject() : invoice,
              parentId: invoiceDoc._id,
              projectInfo: invoiceDoc.project ? {
                _id: invoiceDoc.project._id,
                project_id: invoiceDoc.project.project_id,
                project_title: invoiceDoc.project.project_title,
                users: invoiceDoc.project.users || []
              } : null
            });
          });
        }
      });
      setInvoices(allInvoices);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const getRemainingBalance = (invoice) => {
    if (invoice.remaining_balance != null) {
      return Math.max(0, invoice.remaining_balance);
    }
    const totalAmount = invoice.total_amount || 0;
    const amountPaid = invoice.amount_paid || 0;
    return Math.max(0, totalAmount - amountPaid);
  };

  // Determine payment status (paid, unpaid, partially paid)
  const getPaymentStatus = (invoice) => {
    const totalAmount = invoice.total_amount || 0;
    const amountPaid = invoice.amount_paid || 0;
    const remainingBalance = invoice.remaining_balance != null ? invoice.remaining_balance : totalAmount;

    if (remainingBalance <= 0 || amountPaid >= totalAmount) {
      return 'paid';
    } else if (amountPaid > 0) {
      return 'partially_paid';
    } else {
      return 'unpaid';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
      case 'paid':
        return 'success';
      case 'Partially Paid':
      case 'partially_paid':
        return 'warning';
      case 'Overdue':
      case 'unpaid':
        return 'error';
      case 'Sent':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Group invoices by facility, then by account
  const getInvoicesByFacility = () => {
    const facilityMap = {};
    
    // Initialize facilities with their accounts
    facilities.forEach(facility => {
      const facilityId = facility._id.toString();
      facilityMap[facilityId] = {
        facility: facility,
        accounts: {}
      };
      
      // Initialize all accounts for this facility
      const accounts = facility.accounts_receivable || [];
      accounts.forEach(account => {
        facilityMap[facilityId].accounts[account.account_number] = {
          account: account,
          invoices: []
        };
      });
    });

    // Initialize unmapped
    facilityMap['unmapped'] = {
      facility: null,
      accounts: {
        'unmapped': {
          account: { account_number: 'Unmapped', description: 'Invoices without account mapping' },
          invoices: []
        }
      }
    };

    // Group invoices by facility and account
    invoices.forEach(invoice => {
      if (invoice.account_mapping && invoice.account_mapping.facility_id && invoice.account_mapping.account_number) {
        // Handle both ObjectId and string formats
        const facilityId = typeof invoice.account_mapping.facility_id === 'object' 
          ? invoice.account_mapping.facility_id.toString() 
          : invoice.account_mapping.facility_id;
        const accountNumber = invoice.account_mapping.account_number;
        
        if (facilityMap[facilityId] && facilityMap[facilityId].accounts[accountNumber]) {
          facilityMap[facilityId].accounts[accountNumber].invoices.push(invoice);
        } else {
          // Account might have been deleted, add to unmapped
          facilityMap['unmapped'].accounts['unmapped'].invoices.push(invoice);
        }
      } else {
        facilityMap['unmapped'].accounts['unmapped'].invoices.push(invoice);
      }
    });

    // Apply filters
    let filteredFacilities = Object.values(facilityMap);

    // Filter by facility
    if (selectedFacility !== 'all') {
      filteredFacilities = filteredFacilities.filter(item => {
        if (!item.facility) return true; // Keep unmapped
        // Handle both ObjectId and string formats
        const facilityId = typeof item.facility._id === 'object' 
          ? item.facility._id.toString() 
          : item.facility._id;
        return facilityId === selectedFacility;
      });
    }

    // Filter by date range and search term
    filteredFacilities = filteredFacilities.map(facilityGroup => {
      const filteredAccounts = {};
      
      Object.keys(facilityGroup.accounts).forEach(accountNumber => {
        let accountInvoices = facilityGroup.accounts[accountNumber].invoices;
        
        // Filter by date range
        if (startDate || endDate) {
          accountInvoices = accountInvoices.filter(invoice => {
            const invoiceDate = invoice.generation_date || invoice.billing_period?.start_date;
            if (!invoiceDate) return false;
            const date = new Date(invoiceDate);
            if (startDate && date < startDate) return false;
            if (endDate && date > endDate) return false;
            return true;
          });
        }
        
        // Filter by payment status
        if (paymentStatusFilter !== 'all') {
          accountInvoices = accountInvoices.filter(invoice => {
            const status = getPaymentStatus(invoice);
            return status === paymentStatusFilter;
          });
        }
        
        // Filter by search term (includes user name/email)
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const accountMatchesSearch = (
            accountNumber?.toLowerCase().includes(searchLower) ||
            facilityGroup.accounts[accountNumber].account.description?.toLowerCase().includes(searchLower) ||
            facilityGroup.facility?.facility_name?.toLowerCase().includes(searchLower)
          );
          
          // If account matches search, include all invoices. Otherwise, filter invoices
          if (accountMatchesSearch) {
            // Keep all invoices for this account
          } else {
            accountInvoices = accountInvoices.filter(invoice => {
              // Search in invoice ID
              if (invoice.invoice_id?.toLowerCase().includes(searchLower)) return true;
              // Search in project ID and title
              if (invoice.projectInfo?.project_id?.toLowerCase().includes(searchLower)) return true;
              if (invoice.projectInfo?.project_title?.toLowerCase().includes(searchLower)) return true;
              // Search in user names and emails
              const users = invoice.projectInfo?.users || [];
              for (const user of users) {
                const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
                if (fullName.includes(searchLower)) return true;
                if (user.email?.toLowerCase().includes(searchLower)) return true;
              }
              return false;
            });
          }
        }
        
        // Always include account if it's from a facility (show all facility accounts)
        // For unmapped, only show if there are invoices or search matches
        if (facilityGroup.facility) {
          // Always include facility accounts (even if no invoices)
          filteredAccounts[accountNumber] = {
            ...facilityGroup.accounts[accountNumber],
            invoices: accountInvoices
          };
        } else if (accountInvoices.length > 0 || (searchTerm && (
          accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          facilityGroup.accounts[accountNumber].account.description?.toLowerCase().includes(searchTerm.toLowerCase())
        ))) {
          // For unmapped, only show if there are invoices or search matches
          filteredAccounts[accountNumber] = {
            ...facilityGroup.accounts[accountNumber],
            invoices: accountInvoices
          };
        }
      });
      
      return {
        ...facilityGroup,
        accounts: filteredAccounts
      };
    }).filter(facilityGroup => Object.keys(facilityGroup.accounts).length > 0);

    return filteredFacilities;
  };

  const handleRecordPayment = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.parentId}?populate=true&invoice_id=${invoice.invoice_id}`);
      setSelectedInvoice(response.data);
      setShowRecordPayment(true);
    } catch (error) {
      console.error('Error fetching invoice details for payment:', error);
      setSelectedInvoice(invoice);
      setShowRecordPayment(true);
      toast.error('Unable to load complete invoice data. Basic payment recording available.');
    }
  };

  const handleViewInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);
  };

  const handlePaymentRecorded = () => {
    fetchAllInvoices();
    setShowRecordPayment(false);
    setSelectedInvoice(null);
  };

  const handleViewPDF = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.parentId}/pdf?invoice_id=${invoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.parentId}/pdf?invoice_id=${invoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header if available
      let filename = `invoice-${getInvoiceNumber(invoice.invoice_id)}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const facilityGroups = getInvoicesByFacility();

  return (
    <div>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 500 }}>
        Accounts Receivable
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by invoice, project, user..."
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDate ? startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDate ? endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                label="Payment Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                label="Facility"
              >
                <MenuItem value="all">All Facilities</MenuItem>
                {facilities.map(facility => (
                  <MenuItem key={facility._id} value={facility._id}>
                    {facility.facility_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Facilities, Accounts, and Invoices */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : facilityGroups.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No facilities found</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {facilityGroups.map((facilityGroup, facilityIndex) => {
            const facilityName = facilityGroup.facility?.facility_name || 'Unmapped';
            const accountKeys = Object.keys(facilityGroup.accounts);
            const totalInvoices = accountKeys.reduce((sum, key) => sum + facilityGroup.accounts[key].invoices.length, 0);
            const totalAmount = accountKeys.reduce((sum, key) => 
              sum + facilityGroup.accounts[key].invoices.reduce((acc, inv) => acc + (inv.total_amount || 0), 0), 0);
            const totalUnpaid = accountKeys.reduce((sum, key) => 
              sum + facilityGroup.accounts[key].invoices
                .filter(inv => getPaymentStatus(inv) !== 'paid')
                .reduce((acc, inv) => acc + getRemainingBalance(inv), 0), 0);

            return (
              <Accordion key={facilityIndex} elevation={2} defaultExpanded={facilityGroups.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {facilityName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {accountKeys.length} account(s)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Total Invoices:</strong> {totalInvoices}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Amount:</strong> ${totalAmount.toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Unpaid:</strong> ${totalUnpaid.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 2 }}>
                    {accountKeys.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No accounts with invoices
                      </Typography>
                    ) : (
                      accountKeys.map((accountNumber, accountIndex) => {
                        const accountData = facilityGroup.accounts[accountNumber];
                        const accountInvoices = accountData.invoices;
                        const accountTotal = accountInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
                        const accountUnpaid = accountInvoices
                          .filter(inv => getPaymentStatus(inv) !== 'paid')
                          .reduce((sum, inv) => sum + getRemainingBalance(inv), 0);

                        return (
                          <Accordion key={accountIndex} elevation={1} sx={{ backgroundColor: '#f9f9f9' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    {accountNumber}
                                  </Typography>
                                  {accountData.account.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {accountData.account.description}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>Invoices:</strong> {accountInvoices.length}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Total:</strong> ${accountTotal.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Unpaid:</strong> ${accountUnpaid.toFixed(2)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {accountInvoices.length === 0 ? (
                                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No invoices mapped to this account
                                  </Typography>
                                ) : (
                                  accountInvoices.map((invoice, invIndex) => {
                                    const paymentStatus = getPaymentStatus(invoice);
                                    
                                    return (
                                      <Card key={invIndex} variant="outlined" sx={{ backgroundColor: '#ffffff' }}>
                                        <CardContent>
                                          <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                              <Typography variant="subtitle1">
                                                Invoice #{getInvoiceNumber(invoice.invoice_id)}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Project: {invoice.projectInfo?.project_id || 'N/A'}
                                                {invoice.projectInfo?.project_title && (
                                                  <> - {invoice.projectInfo.project_title}</>
                                                )}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Generated: {formatDate(invoice.generation_date)}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <Typography variant="body2">
                                                <strong>Amount:</strong> ${(invoice.total_amount || 0).toFixed(2)}
                                              </Typography>
                                              <Typography variant="body2">
                                                <strong>Paid:</strong> ${(invoice.amount_paid || 0).toFixed(2)}
                                              </Typography>
                                              <Typography variant="body2">
                                                <strong>Remaining:</strong> ${getRemainingBalance(invoice).toFixed(2)}
                                              </Typography>
                                              <Chip
                                                label={paymentStatus === 'paid' ? 'Paid' : paymentStatus === 'partially_paid' ? 'Partially Paid' : 'Unpaid'}
                                                size="small"
                                                color={getStatusColor(paymentStatus)}
                                                sx={{ mt: 1 }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                                <IconButton aria-label="View details" 
                                                  size="small" 
                                                  color="primary"
                                                  onClick={() => handleViewPDF(invoice)}
                                                  title="View PDF"
                                                >
                                                  <VisibilityIcon />
                                                </IconButton>
                                                <IconButton aria-label="Download" 
                                                  size="small" 
                                                  color="primary"
                                                  onClick={() => handleDownloadPDF(invoice)}
                                                  title="Download PDF"
                                                >
                                                  <DownloadIcon />
                                                </IconButton>
                                                {paymentStatus !== 'paid' && (
                                                  <IconButton aria-label="Record payment" 
                                                    size="small" 
                                                    color="success"
                                                    onClick={() => handleRecordPayment(invoice)}
                                                    title="Record Payment"
                                                  >
                                                    <PaymentIcon />
                                                  </IconButton>
                                                )}
                                                <IconButton aria-label="View invoice" 
                                                  size="small" 
                                                  color="primary"
                                                  onClick={() => handleViewInvoiceDetail(invoice)}
                                                  title="Invoice Details"
                                                >
                                                  <ReceiptLongIcon />
                                                </IconButton>
                                              </Stack>
                                            </Grid>
                                          </Grid>
                                        </CardContent>
                                      </Card>
                                    );
                                  })
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Record Payment Dialog */}
      {selectedInvoice && (
        <RecordPayment
          open={showRecordPayment}
          onClose={() => {
            setShowRecordPayment(false);
            setSelectedInvoice(null);
          }}
          invoice={selectedInvoice}
          onPaymentRecorded={handlePaymentRecorded}
        />
      )}

      {/* View Invoice Details Dialog */}
      {showInvoiceDetail && selectedInvoice && (
        <InvoiceDetail 
          open={showInvoiceDetail}
          onClose={() => setShowInvoiceDetail(false)}
          invoiceId={selectedInvoice.parentId || selectedInvoice._id}
          selectedInvoice={selectedInvoice}
          onUpdate={handlePaymentRecorded}
        />
      )}
    </div>
  );
};

export default AccountsReceivable;

